import React, { useState, useEffect } from "react";
import MarkPopup from "../MarkPopup/MarkPopup";
import { FaCheckCircle, FaTimesCircle, FaSync } from "react-icons/fa";
import config from "../../../../config";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

function StaffDashboard() {
    const [status, setStatus] = useState("idle");
    const [isMarkedIn, setIsMarkedIn] = useState(false);
    const [isMarkedOut, setIsMarkedOut] = useState(false);
    const [markInTime, setMarkInTime] = useState(null);
    const [markOutTime, setMarkOutTime] = useState(null);
    const [attendanceData, setAttendanceData] = useState({
        present: 0,
        total: 0,
    });
    const [serviceTarget, setServiceTarget] = useState({
        target: 0,
        achieved: 0,
    });
    const [productTarget, setProductTarget] = useState({
        target: 0,
        achieved: 0,
    });
    const [advanceData, setAdvanceData] = useState({
        total: 0,
        transactions: [],
    });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const userData = JSON.parse(localStorage.getItem("user"));
    const token = localStorage.getItem("token");
    const bid = localStorage.getItem("branch_id");

    const staffName = userData?.salon_name?.[0] || "Staff";
    const salonName = userData?.salon_name?.[1] || "Salon";
    const userMobile = userData?.user || "";
    const userType = userData?.type || "";

    const today = new Date().toLocaleDateString("en-GB");
    const time = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });

    // Helper function to format time for display
    const formatTimeForDisplay = (timeString) => {
        if (!timeString || timeString === "") return "--";

        try {
            // Handle different time formats from API
            const timeParts = timeString.split(":");
            if (timeParts.length >= 2) {
                const hours = parseInt(timeParts[0]);
                const minutes = timeParts[1];

                // Format to 12-hour format with AM/PM
                const period = hours >= 12 ? "PM" : "AM";
                const displayHours = hours % 12 || 12;

                return `${displayHours}:${minutes} ${period}`;
            }
            return timeString;
        } catch (e) {
            console.error("Error formatting time:", e);
            return timeString;
        }
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            // --- Attendance API ---
            const attendanceRes = await fetch(
                `${
                    config.apiUrl
                }/api/swalook/single_staff_attendance/?branch_name=${bid}&staff_id=${userMobile}&month=${
                    new Date().getMonth() + 1
                }`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const attendance = await attendanceRes.json();

            if (attendance?.status && attendance?.table_data) {
                const staffData =
                    attendance.table_data[staffName] ||
                    Object.values(attendance.table_data)[0] ||
                    {};

                setAttendanceData({
                    present: staffData.number_of_days_present || 0,
                    total: attendance.current_month_days || 0,
                });

                // Reset mark status
                setIsMarkedIn(false);
                setIsMarkedOut(false);
                setMarkInTime(null);
                setMarkOutTime(null);

                // Check if staff has attendance for today
                const today = new Date().toISOString().split("T")[0];
                if (
                    staffData.present_dates &&
                    Array.isArray(staffData.present_dates)
                ) {
                    const todayIndex = staffData.present_dates.findIndex(
                        (date) => date.includes(today)
                    );

                    if (todayIndex !== -1) {
                        setIsMarkedIn(true);

                        // Get in time for today
                        if (
                            staffData.in_time &&
                            staffData.in_time[todayIndex]
                        ) {
                            const inTime = staffData.in_time[todayIndex];
                            const formattedInTime =
                                formatTimeForDisplay(inTime);
                            setMarkInTime(formattedInTime);
                        }

                        // Check if marked out
                        if (
                            staffData.out_time &&
                            staffData.out_time[todayIndex] &&
                            staffData.out_time[todayIndex] !== ""
                        ) {
                            setIsMarkedOut(true);
                            const outTime = staffData.out_time[todayIndex];
                            const formattedOutTime =
                                formatTimeForDisplay(outTime);
                            setMarkOutTime(formattedOutTime);
                        }
                    }
                }
            }

            // --- Targets and Revenue API ---
            const year = new Date().getFullYear();
            const month = String(new Date().getMonth() + 1).padStart(2, "0");

            const targetsRes = await fetch(
                `${config.apiUrl}/api/swalook/sales-targets/?branch_name=${bid}&type=${userType}&year=${year}&month=${month}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const targetsData = await targetsRes.json();

            let serviceTarget = 0;
            let serviceAchieved = 0;
            let productTarget = 0;
            let productAchieved = 0;

            if (targetsData.list?.[0]?.staff_targets) {
                try {
                    const staffTargets = JSON.parse(
                        targetsData.list[0].staff_targets.replace(/'/g, '"')
                    );
                    const currentStaffTarget = staffTargets.find(
                        (staff) =>
                            staff.staff_name.toLowerCase() ===
                            staffName.toLowerCase()
                    );

                    if (currentStaffTarget) {
                        serviceTarget =
                            parseInt(currentStaffTarget.staff_target) || 0;
                        productTarget = 0;
                    }
                } catch (e) {
                    console.error("Error parsing staff targets:", e);
                }
            }

            if (targetsData.staff_revenue?.branches?.[0]?.staff_data) {
                const currentStaffData =
                    targetsData.staff_revenue.branches[0].staff_data.find(
                        (staff) =>
                            staff.staff_name
                                .toLowerCase()
                                .replace(/[^a-z]/g, "") ===
                            staffName.toLowerCase().replace(/[^a-z]/g, "")
                    );

                if (currentStaffData) {
                    serviceAchieved =
                        parseInt(currentStaffData.total_sales) || 0;
                    productAchieved = 0;
                }
            }

            setServiceTarget({
                target: serviceTarget,
                achieved: serviceAchieved,
            });

            setProductTarget({
                target: productTarget,
                achieved: productAchieved,
            });

            // --- Advance Salary API ---
            const advanceRes = await fetch(
                `${config.apiUrl}/api/swalook/single_staff_advance/?branch_name=${bid}&staff_id=${userMobile}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const advanceResponse = await advanceRes.json();

            if (advanceResponse?.status && advanceResponse?.table_data) {
                const staffAdvance = advanceResponse.table_data[0] || {};
                setAdvanceData({
                    total: staffAdvance.current_month_total || 0,
                    transactions: staffAdvance.advances || [],
                });
            }
        } catch (error) {
            console.error("Dashboard fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [bid, token, userMobile]);

    const handleMarkAction = () => {
        setStatus(isMarkedIn ? "markout" : "markin");
    };

    const resetPopup = () => setStatus("idle");

    const handleMarkSuccess = () => {
        if (status === "markin") {
            setIsMarkedIn(true);
            setMarkInTime(time);
        } else if (status === "markout") {
            setIsMarkedOut(true);
            setMarkOutTime(time);
        }
        setStatus("success");
        // Refresh dashboard data after successful mark-in/out
        setTimeout(() => {
            fetchDashboardData();
        }, 1500);
    };

    const handleClosePopup = () => {
        setStatus("idle");
    };

    const handleLogout = () => {
        Cookies.remove("loggedIn");
        Cookies.remove("type");
        Cookies.remove("salonName");
        Cookies.remove("branch_n");
        Cookies.remove("salon-name");
        navigate("/staff/login");
    };

    const handleRefresh = () => {
        fetchDashboardData();
    };

    return (
        <div
            className="bg-gray-200 max-h-full min-h-screen flex flex-col justify-between px-4 py-6"
            style={{ zoom: 1.33 }}
        >
            <div className="flex flex-col min-h-screen pb-4">
                <div className="mb-4 flex justify-between items-center">
                    <div>
                        <p className="text-sm text-gray-600">{salonName}</p>
                        <h1 className="text-lg font-bold">
                            Welcome, {staffName}
                        </h1>
                    </div>
                    <div className="flex flex-col items-end">
                        <div className="flex space-x-2 mb-2">
                            <button
                                onClick={handleRefresh}
                                disabled={loading}
                                className="px-3 py-2 bg-blue-500 text-white rounded-full text-xs font-medium hover:bg-blue-600 flex items-center"
                            >
                                <FaSync
                                    className={
                                        loading ? "animate-spin mr-1" : "mr-1"
                                    }
                                />
                                Refresh
                            </button>
                            <button
                                onClick={handleLogout}
                                className="px-3 py-2 bg-red-500 text-white rounded-full text-xs font-medium hover:bg-red-600"
                            >
                                Logout
                            </button>
                        </div>
                        <p className="text-sm text-gray-600">
                            {time} {today}
                        </p>
                    </div>
                </div>

                {loading && (
                    <div className="flex justify-center mb-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                )}

                {isMarkedIn && (
                    <div className="bg-white rounded-2xl p-4 mb-4 text-center">
                        <p className="font-medium text-left text-lg mb-1">
                            Today's Time
                        </p>
                        <div className="mb-1">
                            <div>In - Time</div>
                            <div className="text-blue-500">
                                {markInTime || "--"}
                            </div>
                        </div>
                        <div>
                            <div>Out - Time</div>
                            <div className="text-blue-500">
                                {markOutTime || "--"}
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl p-4 mb-4 text-center">
                    <p className="font-medium text-left text-lg mb-1">
                        {new Date().toLocaleString("default", {
                            month: "long",
                        })}
                        , {new Date().getFullYear()}
                    </p>

                    <div className="mb-1">
                        <div>Present Days</div>
                        <div className="text-blue-500">
                            {attendanceData.present}
                        </div>
                    </div>
                    <div>
                        <div>Total Days</div>
                        <div className="text-blue-500">
                            {attendanceData.total}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl p-4 mb-4 text-center">
                    <p className="font-medium text-left text-lg mb-1">
                        Targets
                    </p>
                    <div className="mb-1">
                        <div>Total</div>
                        <div className="text-blue-500">
                            Rs. {serviceTarget.target}
                        </div>
                    </div>
                    <div>
                        <div>Achieved</div>
                        <div className="text-blue-500">
                            Rs. {serviceTarget.achieved}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl p-4 mb-4">
                    <p className="font-medium text-left text-lg mb-2">
                        Advance Salary
                    </p>
                    <div className="mb-3">
                        <div className="flex justify-between mb-1">
                            <span>Total Advances:</span>
                            <span className="text-blue-500">
                                Rs. {advanceData.total.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    {advanceData.transactions.length > 0 ? (
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-left">
                                Recent Transactions:
                            </p>
                            {advanceData.transactions
                                .slice(0, 2)
                                .map((advance, index) => (
                                    <div
                                        key={index}
                                        className="p-2 border rounded-lg"
                                    >
                                        <div className="flex justify-between text-sm">
                                            <span>Amount:</span>
                                            <span className="text-blue-500">
                                                Rs.{" "}
                                                {advance.advance_amount.toFixed(
                                                    2
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-500">
                                            <span>Date:</span>
                                            <span>
                                                {new Date(
                                                    advance.created_at
                                                ).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">
                            No advance transactions
                        </p>
                    )}
                </div>

                <div className="my-auto py-4">
                    <button
                        onClick={handleMarkAction}
                        disabled={isMarkedIn && isMarkedOut}
                        className={`w-full py-3 text-white rounded-full text-lg font-medium transition ${
                            isMarkedIn && isMarkedOut
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                        {isMarkedIn
                            ? isMarkedOut
                                ? "Already Marked"
                                : "Mark Out"
                            : "Mark In"}
                    </button>
                </div>
            </div>

            {(status === "markin" || status === "markout") && (
                <MarkPopup
                    onClose={handleClosePopup}
                    onSuccess={handleMarkSuccess}
                    staffId={userData?.id}
                    type={status}
                    userMobile={userMobile}
                    config={config}
                />
            )}

            {status === "success" && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-end z-50">
                    <div className="bg-white w-full rounded-t-2xl p-6">
                        <h3 className="font-semibold mb-4">
                            {isMarkedOut
                                ? "Mark Out Successful"
                                : "Mark In Successful"}
                        </h3>
                        <div className="flex justify-center text-center mb-4">
                            <FaCheckCircle className="text-green-500 text-4xl" />
                        </div>
                        <p className="text-lg font-medium text-center mb-1">
                            {isMarkedOut
                                ? `Out-time marked at ${markOutTime}`
                                : `In-time marked at ${markInTime}`}
                        </p>
                        <p className="text-lg text-center text-gray-500">
                            Attendance recorded successfully.
                        </p>
                        <button
                            onClick={resetPopup}
                            className="w-full mt-4 py-2 bg-blue-600 text-white rounded-md text-lg font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {status === "fail" && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-end z-50">
                    <div className="bg-white w-full rounded-t-2xl p-6">
                        <h3 className="font-semibold mb-4">
                            {status === "markout"
                                ? "Mark Out Failed"
                                : "Mark In Failed"}
                        </h3>
                        <div className="flex justify-center text-center mb-4">
                            <FaTimesCircle className="text-red-500 text-4xl" />
                        </div>
                        <p className="text-lg font-medium text-center mb-1">
                            Failed to record attendance.
                        </p>
                        <p className="text-center text-gray-500">
                            Please check your location and try again.
                        </p>
                        <button
                            onClick={resetPopup}
                            className="w-full mt-4 py-2 bg-blue-600 text-white rounded-md text-lg font-medium"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default StaffDashboard;
